import logger from "@logger";
import type { CroppedAreaPixels } from "@modules/photoUpload/types";
import { createCroppedImage } from "@modules/photoUpload/utils/imageProcessing";
import { useState } from "react";
import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";

export interface PhotoCropModalProps {
  image: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
}

/**
 * Photo Crop Modal Component
 *
 * A modal component for cropping profile photos using react-easy-crop.
 * Provides touch-based zoom with pinch gestures and drag to reposition.
 *
 * Features:
 * - Rectangular crop area (aspect ratio 3:4)
 * - Touch-based zoom (pinch to zoom)
 * - Drag to reposition crop area
 * - Real-time preview
 * - Processing state during image processing
 * - Error handling for image processing failures
 *
 * @param image - Base64 or URL of the image to crop
 * @param onCropComplete - Callback when crop is completed with the cropped image blob
 * @param onCancel - Callback when crop is cancelled
 */
export function PhotoCropModal({ image, onCropComplete, onCancel }: PhotoCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCropComplete = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsProcessing(true);

    try {
      const croppedImageBlob = await createCroppedImage(image, croppedAreaPixels, 0);
      onCropComplete(croppedImageBlob);
    } catch (error) {
      logger.error("Failed to process cropped image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCropChange = (newCrop: { x: number; y: number }) => {
    setCrop(newCrop);
  };

  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom);
  };

  const handleCropAreaChange = (_: Area, croppedAreaPixelsValue?: Area | null) => {
    if (!croppedAreaPixelsValue) {
      return;
    }

    setCroppedAreaPixels(croppedAreaPixelsValue as CroppedAreaPixels);
  };

  const isCropButtonDisabled = !croppedAreaPixels || isProcessing;

  return (
    <div
      data-testid="photo-crop-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div data-testid="modal-content" className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">Crop Your Photo</h2>
          <p className="text-sm text-gray-600">Pinch to zoom, drag to reposition, then tap Crop</p>
        </div>

        {/* Crop Area */}
        <div className="relative mb-6" style={{ height: "400px" }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={0}
            aspect={3 / 4}
            cropShape="rect"
            showGrid={false}
            onCropChange={handleCropChange}
            onZoomChange={handleZoomChange}
            onCropAreaChange={handleCropAreaChange}
          />
        </div>

        {/* Processing Indicator */}
        {isProcessing && (
          <div data-testid="processing-indicator" className="mb-4 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-lg">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-800"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-label="Loading spinner"
              >
                <title>Loading spinner</title>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing...
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            data-testid="cancel-button"
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            data-testid="crop-button"
            type="button"
            onClick={handleCropComplete}
            disabled={isCropButtonDisabled}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-pink-600 border border-transparent rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Crop
          </button>
        </div>
      </div>
    </div>
  );
}

export default PhotoCropModal;
